// Screen 9 — Master ledger (multi-tenant).
//
// Every tenant's ledger entries in one dense table: party (client), type, a
// description with a Manual flag, the source (AUTO system-posted vs MANUAL
// operator + reason), and the credit/debit money columns. Three operator
// actions open modals wired to the REAL finance handlers
// (internal/admin/finance.go — the source of truth):
//
//   • Manual adjustment — POST /admin/ledger/adjustments
//     { client_id, entry_date, debit, credit, reason (REQUIRED), label }
//     → 201 ledgerEntryResponse. One visibly-flagged manual row.
//   • Auto-priced entry — POST /admin/ledger/entries
//     { client_id, agency_id, entry_date, top_up_amount, label }
//     → 201 { rows: [4 settlement rows], bd_profit }. THE SERVER PRICES:
//     it resolves the client's monthly commission tier + the agency fee and
//     returns the 4 posted rows — rendered verbatim as the confirmation.
//     There is NO client-side tier table or preview arithmetic.
//   • Top-up (operator splits) — POST /admin/ledger/topup
//     { client_id, agency_id?, entry_date, client_topup, nexova_commission,
//       agency_topup, agency_commission, commission_pct?, label }
//     → 201 { rows: [4 rows posted verbatim] }.
//
// Live clients and agencies are fetched from /admin/clients and
// /admin/agencies — never hardcoded.
//
// MONEY: all amounts are exact decimal STRINGS rendered via MoneyCell — never
// parsed to float. Pricing happens SERVER-SIDE (big.Rat); this UI only relays
// operator-entered decimal strings and renders what the backend returns.

import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";

import { api } from "@/api/client";
import { errMessage } from "@/lib/errMessage";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  DataTable,
  MoneyCell,
  Modal,
  ToneBadge,
  type Column,
  type Tone,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { fmtMoney } from "./money";
import { NativeSelect, Pager, Segmented } from "./controls";

// The real backend row (internal/admin/finance.go ledgerEntryResponse). We map
// this into the dense display row below; there is NO client NAME on the row, so
// we resolve it from /admin/clients.
interface ApiLedgerEntry {
  id: number;
  entry_date: string;
  label: string;
  credit: string;
  debit: string;
  type: string;
  commission_pct: string;
  client_id: string;
  agency_id: string;
  source: string;
  is_manual: boolean;
  created_by: string;
  reason: string;
}
interface ApiLedgerList {
  entries: ApiLedgerEntry[];
}

// The 4-row settlement post response ({rows, bd_profit}) shared by
// /admin/ledger/entries (auto-priced) and /admin/ledger/topup (verbatim).
interface TopUpResponse {
  rows: ApiLedgerEntry[];
  bd_profit?: string;
}

// The display row.
interface LedgerEntry {
  id: string;
  date: string;
  client_id: string;
  client: string;
  type: string;
  label: string;
  credit: string;
  debit: string;
  source: "AUTO" | "MANUAL";
  actor: string;
  reason: string;
}

interface ClientRef {
  id: string;
  name: string;
}
interface AgencyRef {
  id: string;
  name: string;
}

// The backend ledger `type` taxonomy (store.LedgerEntry.Type) — drives the
// server-side ?type filter.
const LEDGER_TYPES = [
  "client_topup",
  "nexova_commission",
  "agency_topup",
  "agency_commission",
  "account_topup",
  "manual_adjustment",
  "account_sale",
  "net_profit",
  "reversal",
] as const;

const PAGE_SIZE = 50;

// Collapse the backend ledger `type` taxonomy into the display tone bucket.
function displayType(type: string): string {
  switch (type) {
    case "client_topup":
      return "topup";
    case "nexova_commission":
    case "commission":
      return "commission";
    case "manual_adjustment":
    case "reversal":
      return "adjustment";
    case "agency_topup":
    case "account_topup":
    case "agency_settlement":
      return "agency";
    default:
      return type || "entry";
  }
}

function typeTone(type: string): Tone {
  switch (type) {
    case "topup":
      return "credit";
    case "commission":
      return "profit";
    case "adjustment":
      return "manual";
    case "agency":
    case "settlement":
      return "debit";
    default:
      return "na";
  }
}

// Today's date in the backend's entry_date shape (YYYY-MM-DD, local time).
function today(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

// The zod money string shape shared by every amount field.
const moneyString = (msg: string) =>
  z.string().regex(/^\d+(\.\d{1,2})?$/, msg);

export function LedgerPage() {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Live entity lists for the write modals (never hardcoded).
  const [clients, setClients] = useState<ClientRef[]>([]);
  const [agencies, setAgencies] = useState<AgencyRef[]>([]);

  // Server-side ?type filter + offset pager.
  const [typeF, setTypeF] = useState("");
  const [offset, setOffset] = useState(0);
  const [rawCount, setRawCount] = useState(0);

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (typeF) qs.set("type", typeF);
      qs.set("limit", String(PAGE_SIZE));
      qs.set("offset", String(offset));
      const res = await api.get<ApiLedgerList>(
        `/admin/ledger/entries?${qs.toString()}`,
      );
      const c = await api
        .get<{ clients: ClientRef[] }>("/admin/clients")
        .catch(() => null);
      const cs = c?.clients ?? [];
      setClients(cs);
      const nameOf = (cid: string) =>
        cs.find((cl) => cl.id === cid)?.name ?? cid ?? "—";
      const rows = res.entries ?? [];
      setRawCount(rows.length);
      setEntries(
        rows.map((e) => ({
          id: String(e.id),
          date: e.entry_date,
          client_id: e.client_id,
          client: e.client_id ? nameOf(e.client_id) : "—",
          type: displayType(e.type),
          label: e.label,
          credit: e.credit,
          debit: e.debit,
          source: e.is_manual ? "MANUAL" : "AUTO",
          actor: e.created_by,
          reason: e.reason,
        })),
      );
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  }, [typeF, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  // Agencies are only needed by the write modals — fetched tolerantly once.
  useEffect(() => {
    void api
      .get<{ agencies: AgencyRef[] }>("/admin/agencies")
      .then((a) => setAgencies(a.agencies ?? []))
      .catch(() => {});
  }, []);

  const columns: Column<LedgerEntry>[] = [
    {
      key: "date",
      header: "Date",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          {r.date}
        </span>
      ),
    },
    {
      key: "id",
      header: "Entry",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-3 tabular-nums">
          {r.id}
        </span>
      ),
    },
    {
      key: "client",
      header: "Party",
      cell: (r) => <span className="font-medium text-nx-text">{r.client}</span>,
    },
    {
      key: "type",
      header: "Type",
      cell: (r) => (
        <ToneBadge tone={typeTone(r.type)} className="capitalize">
          {r.type}
        </ToneBadge>
      ),
    },
    {
      key: "label",
      header: "Description",
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span className="text-nx-text-2">{r.label}</span>
          {r.source === "MANUAL" && (
            <ToneBadge tone="manual">Manual</ToneBadge>
          )}
        </div>
      ),
    },
    {
      key: "source",
      header: "Source",
      cell: (r) => (
        <div className="leading-tight">
          <span
            className={cn(
              "font-mono text-[0.7rem] font-semibold tracking-wide",
              r.source === "AUTO" ? "text-nx-text-3" : "text-nx-manual",
            )}
            title={
              r.source === "MANUAL" && r.reason
                ? `${r.actor}: ${r.reason}`
                : "System-posted"
            }
          >
            {r.source}
          </span>
          {/* created_by is an OPAQUE actor string — a portal user email, or a
              service principal like "svc:ops-agent". Never assumed to be a
              UUID; rendered verbatim (truncated, full value in the title). */}
          {r.actor && (
            <span
              className="block max-w-[11rem] truncate font-mono text-[0.65rem] text-nx-text-3"
              title={r.actor}
            >
              {r.actor}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "credit",
      header: "Credit",
      align: "right",
      cell: (r) =>
        r.credit !== "0.00" ? (
          <MoneyCell value={r.credit} tone="credit" signed="+" />
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "debit",
      header: "Debit",
      align: "right",
      cell: (r) =>
        r.debit !== "0.00" ? (
          <MoneyCell value={r.debit} tone="debit" signed="-" />
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">Master ledger</h2>
          <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
            Every tenant&apos;s entries.{" "}
            <span className="text-nx-text-3">AUTO</span> are system-posted;{" "}
            <span className="text-nx-manual">Manual</span> are operator
            adjustments with a recorded reason.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setAdjustOpen(true)}>
            Manual adjustment
          </Button>
          <Button variant="outline" size="sm" onClick={() => setTopupOpen(true)}>
            Top-up (splits)
          </Button>
          <Button size="sm" onClick={() => setEntryOpen(true)}>
            New entry
          </Button>
        </div>
      </div>

      {/* Type filter — applied SERVER-SIDE via ?type. */}
      <div className="mb-4 max-w-xs">
        <NativeSelect
          aria-label="Filter by type"
          value={typeF}
          onChange={(e) => {
            setTypeF(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">All types</option>
          {LEDGER_TYPES.map((t) => (
            <option key={t} value={t}>
              {t.replace(/_/g, " ")}
            </option>
          ))}
        </NativeSelect>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load the ledger</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={entries}
            rowKey={(r) => r.id}
            empty="No ledger entries yet."
          />
          <Pager
            offset={offset}
            pageSize={PAGE_SIZE}
            count={rawCount}
            loading={loading}
            onOffset={setOffset}
          />
        </>
      )}

      <ManualAdjustmentModal
        open={adjustOpen}
        clients={clients}
        onOpenChange={setAdjustOpen}
        onPosted={load}
      />
      <TopUpModal
        open={topupOpen}
        clients={clients}
        agencies={agencies}
        onOpenChange={setTopupOpen}
        onPosted={load}
      />
      <NewEntryModal
        open={entryOpen}
        clients={clients}
        agencies={agencies}
        onOpenChange={setEntryOpen}
        onPosted={load}
      />
    </AdminShell>
  );
}

// ---- Manual adjustment (POST /admin/ledger/adjustments) ----------------------

const adjustSchema = z.object({
  client_id: z.string().min(1, "Select a client"),
  direction: z.enum(["credit", "debit"]),
  amount: moneyString("Enter an amount like 150.00"),
  reason: z.string().min(3, "A reason is required (audit-logged)"),
  label: z.string(),
});
type AdjustValues = z.infer<typeof adjustSchema>;

function ManualAdjustmentModal({
  open,
  clients,
  onOpenChange,
  onPosted,
}: {
  open: boolean;
  clients: ClientRef[];
  onOpenChange: (open: boolean) => void;
  onPosted: () => void | Promise<void>;
}) {
  const form = useForm<AdjustValues>({
    resolver: zodResolver(adjustSchema),
    defaultValues: {
      client_id: "",
      direction: "credit",
      amount: "",
      reason: "",
      label: "",
    },
  });

  async function onSubmit(values: AdjustValues) {
    try {
      // Backend shape (adjustmentRequest): debit/credit are separate decimal
      // strings; reason is REQUIRED; created_by is the JWT server-side.
      const amount = fmtMoney(values.amount);
      await api.post("/admin/ledger/adjustments", {
        client_id: values.client_id,
        entry_date: today(),
        debit: values.direction === "debit" ? amount : "0",
        credit: values.direction === "credit" ? amount : "0",
        reason: values.reason,
        label: values.label,
      });
      toast.success("Manual adjustment posted — visible to the client");
      onOpenChange(false);
      form.reset();
      await onPosted();
    } catch (err) {
      toast.error(errMessage(err));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) form.reset();
      }}
      title="Manual adjustment"
      description="Audit-logged and visible to the client as a Manual entry."
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="grid gap-4"
          noValidate
        >
          <FormField
            control={form.control}
            name="client_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Client</FormLabel>
                <FormControl>
                  <NativeSelect {...field}>
                    <option value="">Select a client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </NativeSelect>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="direction"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Direction</FormLabel>
                <FormControl>
                  <Segmented
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: "credit", label: "Credit" },
                      { value: "debit", label: "Debit" },
                    ]}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount (USD)</FormLabel>
                <FormControl>
                  <Input inputMode="decimal" placeholder="150.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason (required)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. Service credit for delayed account share"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="label"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (optional)</FormLabel>
                <FormControl>
                  <Input placeholder="Shown on the ledger row" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              Post adjustment
            </Button>
          </div>
        </form>
      </Form>
    </Modal>
  );
}

// ---- Posted-rows confirmation (server response, rendered verbatim) -----------

function PostedRowsPanel({
  rows,
  bdProfit,
}: {
  rows: ApiLedgerEntry[];
  bdProfit?: string;
}) {
  return (
    <div className="rounded-md border border-nx-border bg-nx-bg-2 p-3">
      <p className="mb-2 text-xs font-medium tracking-wide text-nx-text-3 uppercase">
        Posted (server-priced)
      </p>
      <ul className="grid gap-1.5">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-4">
            <span className="text-sm text-nx-text-2">
              {r.type.replace(/_/g, " ")}
              {r.commission_pct ? ` (${r.commission_pct}%)` : ""}
            </span>
            {r.debit !== "0.00" && r.debit !== "0" ? (
              <MoneyCell value={r.debit} tone="debit" signed="-" />
            ) : (
              <MoneyCell value={r.credit} tone="credit" signed="+" />
            )}
          </li>
        ))}
        {bdProfit ? (
          <li className="flex items-center justify-between gap-4 border-t border-nx-border pt-1.5">
            <span className="text-sm text-nx-text-2">BD profit (derived)</span>
            <MoneyCell value={bdProfit} tone="profit" />
          </li>
        ) : null}
      </ul>
    </div>
  );
}

// ---- Top-up with operator-supplied splits (POST /admin/ledger/topup) ---------

const topupSchema = z.object({
  client_id: z.string().min(1, "Select a client"),
  agency_id: z.string(),
  client_topup: moneyString("Enter an amount like 5000.00"),
  nexova_commission: moneyString("Enter an amount like 300.00"),
  agency_topup: moneyString("Enter an amount like 4700.00"),
  agency_commission: moneyString("Enter an amount like 235.00"),
  commission_pct: z.string(),
  label: z.string(),
});
type TopUpValues = z.infer<typeof topupSchema>;

function TopUpModal({
  open,
  clients,
  agencies,
  onOpenChange,
  onPosted,
}: {
  open: boolean;
  clients: ClientRef[];
  agencies: AgencyRef[];
  onOpenChange: (open: boolean) => void;
  onPosted: () => void | Promise<void>;
}) {
  const form = useForm<TopUpValues>({
    resolver: zodResolver(topupSchema),
    defaultValues: {
      client_id: "",
      agency_id: "",
      client_topup: "",
      nexova_commission: "",
      agency_topup: "",
      agency_commission: "",
      commission_pct: "",
      label: "",
    },
  });
  // The server's posted rows — the confirmation view after a 201.
  const [posted, setPosted] = useState<TopUpResponse | null>(null);

  function close(o: boolean) {
    onOpenChange(o);
    if (!o) {
      form.reset();
      setPosted(null);
    }
  }

  async function onSubmit(values: TopUpValues) {
    try {
      // Backend shape (topUpRequest): the 4 split amounts are posted VERBATIM
      // as the settlement rows — no client-side pricing.
      const res = await api.post<TopUpResponse>("/admin/ledger/topup", {
        client_id: values.client_id,
        agency_id: values.agency_id,
        entry_date: today(),
        client_topup: fmtMoney(values.client_topup),
        nexova_commission: fmtMoney(values.nexova_commission),
        agency_topup: fmtMoney(values.agency_topup),
        agency_commission: fmtMoney(values.agency_commission),
        commission_pct: values.commission_pct,
        label: values.label,
      });
      setPosted(res);
      toast.success("Top-up posted (4 settlement rows)");
      await onPosted();
    } catch (err) {
      toast.error(errMessage(err));
    }
  }

  const moneyFields: { name: keyof TopUpValues; label: string }[] = [
    { name: "client_topup", label: "Client top-up (credit)" },
    { name: "nexova_commission", label: "Nexova commission (credit)" },
    { name: "agency_topup", label: "Agency top-up (debit)" },
    { name: "agency_commission", label: "Agency commission (credit)" },
  ];

  return (
    <Modal
      open={open}
      onOpenChange={close}
      title="Top-up (operator splits)"
      description="Posts the 4 settlement rows exactly as entered (e.g. derived in an external sheet). For server pricing use New entry."
    >
      {posted ? (
        <div className="grid gap-4">
          <PostedRowsPanel rows={posted.rows} bdProfit={posted.bd_profit} />
          <div className="flex justify-end">
            <Button onClick={() => close(false)}>Done</Button>
          </div>
        </div>
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid gap-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="client_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client</FormLabel>
                  <FormControl>
                    <NativeSelect {...field}>
                      <option value="">Select a client…</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="agency_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agency (optional)</FormLabel>
                  <FormControl>
                    <NativeSelect {...field}>
                      <option value="">No agency</option>
                      {agencies.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              {moneyFields.map((mf) => (
                <FormField
                  key={mf.name}
                  control={form.control}
                  name={mf.name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{mf.label}</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="decimal"
                          placeholder="0.00"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormField
              control={form.control}
              name="commission_pct"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Commission % (optional)</FormLabel>
                  <FormControl>
                    <Input inputMode="decimal" placeholder="6.5" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="June settlement" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Post top-up
              </Button>
            </div>
          </form>
        </Form>
      )}
    </Modal>
  );
}

// ---- New entry: auto-priced settlement (POST /admin/ledger/entries) ----------

const entrySchema = z.object({
  client_id: z.string().min(1, "Select a client"),
  agency_id: z.string().min(1, "An agency is required to price the top-up"),
  amount: moneyString("Enter an amount like 1000.00"),
  label: z.string(),
});
type EntryValues = z.infer<typeof entrySchema>;

function NewEntryModal({
  open,
  clients,
  agencies,
  onOpenChange,
  onPosted,
}: {
  open: boolean;
  clients: ClientRef[];
  agencies: AgencyRef[];
  onOpenChange: (open: boolean) => void;
  onPosted: () => void | Promise<void>;
}) {
  const form = useForm<EntryValues>({
    resolver: zodResolver(entrySchema),
    defaultValues: { client_id: "", agency_id: "", amount: "", label: "" },
  });
  // The server's priced rows — rendered as THE confirmation. There is no
  // client-side preview: the commission tier and agency fee live server-side.
  const [posted, setPosted] = useState<TopUpResponse | null>(null);

  function close(o: boolean) {
    onOpenChange(o);
    if (!o) {
      form.reset();
      setPosted(null);
    }
  }

  async function onSubmit(values: EntryValues) {
    try {
      // Backend shape (postEntryRequest): the server resolves the client's
      // monthly commission tier + the agency fee and posts 4 rows atomically.
      const res = await api.post<TopUpResponse>("/admin/ledger/entries", {
        client_id: values.client_id,
        agency_id: values.agency_id,
        entry_date: today(),
        top_up_amount: fmtMoney(values.amount),
        label: values.label,
      });
      setPosted(res);
      toast.success("Settlement posted — priced by the server");
      await onPosted();
    } catch (err) {
      toast.error(errMessage(err));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={close}
      title="New entry (auto-priced)"
      description="The server resolves the client's monthly commission tier and the agency fee, then posts the 4 settlement rows."
    >
      {posted ? (
        <div className="grid gap-4">
          <PostedRowsPanel rows={posted.rows} bdProfit={posted.bd_profit} />
          <div className="flex justify-end">
            <Button onClick={() => close(false)}>Done</Button>
          </div>
        </div>
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="grid gap-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="client_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client</FormLabel>
                  <FormControl>
                    <NativeSelect {...field}>
                      <option value="">Select a client…</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="agency_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agency (prices the settlement)</FormLabel>
                  <FormControl>
                    <NativeSelect {...field}>
                      <option value="">Select an agency…</option>
                      {agencies.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </NativeSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Top-up amount (USD)</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="decimal"
                      placeholder="1000.00"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Client top-up — bank wire"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="text-xs text-nx-text-3">
              Commission is priced server-side from the client&apos;s monthly
              tier — the posted rows are shown as the confirmation.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Post entry
              </Button>
            </div>
          </form>
        </Form>
      )}
    </Modal>
  );
}
